<template>
  <div class="stock-ad">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21M3 6H21M3 18H21M7 12L9 8L13 14L17 10L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>AD累积/派发线分析</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">基于收盘价位置的资金流向分析系统</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="selectedCode"
        placeholder="选择股票"
        filterable
        clearable
        class="filter-select"
        :loading="stockListLoading"
      >
        <el-option
          v-for="item in stockCodeList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="开始日期"
        value-format="yyyy-MM-dd"
        class="date-picker"
      >
      </el-date-picker>

      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="结束日期"
        value-format="yyyy-MM-dd"
        class="date-picker"
      >
      </el-date-picker>

      <el-button type="primary" @click="fetchData" :loading="isQuerying">
        <i class="el-icon-search"></i>
        查询
      </el-button>
      <el-button @click="resetFilters">
        <i class="el-icon-refresh-left"></i>
        重置
      </el-button>
    </div>

    <!-- 单股票图表视图 -->
    <div v-if="selectedCode" class="chart-container">
      <!-- ECharts 图表 -->
      <div ref="adChart" class="ad-chart"></div>

      <!-- 数据表格（可折叠） -->
      <el-collapse v-model="tableVisible" class="table-collapse">
        <el-collapse-item title="查看详细数据" name="table">
          <el-table
            :data="stockList"
            border
            stripe
            style="width: 100%"
            class="stock-table"
            :default-sort="{ prop: 'date', order: 'descending' }"
          >
            <el-table-column prop="date" label="日期" width="120"></el-table-column>
            <el-table-column prop="close" label="收盘价" width="100" align="right"></el-table-column>
            <el-table-column prop="high" label="最高价" width="100" align="right"></el-table-column>
            <el-table-column prop="low" label="最低价" width="100" align="right"></el-table-column>
            <el-table-column prop="volume" label="成交量" width="120" align="right">
              <template slot-scope="scope">
                {{ formatVolume(scope.row.volume) }}
              </template>
            </el-table-column>
            <el-table-column prop="clv" label="CLV" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getCLVClass(scope.row.clv)">
                  {{ formatCLV(scope.row.clv) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="adl" label="ADL" width="140" align="right">
              <template slot-scope="scope">
                <span :class="getADLClass(scope.row.adl, scope.row.adl_ma5)">
                  {{ formatADL(scope.row.adl) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="adl_ma5" label="ADL MA5" width="140" align="right">
              <template slot-scope="scope">
                {{ formatADL(scope.row.adl_ma5) }}
              </template>
            </el-table-column>
            <el-table-column prop="adl_change" label="ADL变化" width="120" align="right">
              <template slot-scope="scope">
                <span :class="getValueClass(scope.row.adl_change)">
                  {{ scope.row.adl_change ? (scope.row.adl_change > 0 ? '+' : '') + formatADL(scope.row.adl_change) : '-' }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state">
      <i class="el-icon-data-analysis"></i>
      <p>请选择股票开始分析</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'StockAD',
  data() {
    // 获取当前日期，格式化为 YYYY-MM-DD
    const today = new Date()
    const endDate = today.toISOString().split('T')[0]

    return {
      selectedCode: '',
      startDate: '2025-01-01',
      endDate: endDate,
      stockCodeList: [],
      stockListLoading: false,
      stockList: [],
      isQuerying: false,
      currentStockInfo: {},
      currentData: {},
      tableVisible: '',
      chart: null,
      storageKey: 'selected_stock_code'
    }
  },
  mounted() {
    this.loadStockCodeList()
    this.restoreSelectedCode()
  },
  watch: {
    // 监听路由参数变化，支持直接在URL输入股票代码
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode && newCode !== this.selectedCode) {
          this.selectedCode = newCode
          // 更新localStorage
          localStorage.setItem(this.storageKey, newCode)
          // 获取数据
          this.fetchData()
        }
      }
    },
    selectedCode(newVal) {
      if (newVal) {
        localStorage.setItem(this.storageKey, newVal)
        // 同步更新URL
        if (this.$route.query.code !== newVal) {
          this.$router.replace({ query: { code: newVal } })
        }
      }
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    async loadStockCodeList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockCodeList = response.data.data
        }
      } catch (error) {
        this.$message.error('加载股票列表失败')
        console.error(error)
      } finally {
        this.stockListLoading = false
      }
    },

    restoreSelectedCode() {
      // 优先从URL参数读取，其次从localStorage读取
      const urlCode = this.$route.query.code
      const savedCode = localStorage.getItem(this.storageKey)
      const code = urlCode || savedCode

      if (code) {
        this.selectedCode = code
        // 同步更新URL和localStorage
        localStorage.setItem(this.storageKey, code)
        if (this.$route.query.code !== code) {
          this.$router.replace({ query: { code } })
        }
      }
    },

    async fetchData() {
      if (!this.selectedCode) {
        this.$message.warning('请选择股票')
        return
      }

      this.isQuerying = true

      try {
        const params = {}

        // 添加日期范围参数
        if (this.startDate) {
          params.start_date = this.startDate
        }
        if (this.endDate) {
          params.end_date = this.endDate
        }

        const response = await axios.get(`/stock/ad/${this.selectedCode}`, {
          params
        })

        // 检查响应数据
        if (!response.data) {
          throw new Error('后端返回数据为空')
        }

        if (response.data.status === 'success') {
          this.stockList = response.data.data || []

          if (this.stockList.length > 0) {
            this.currentData = this.stockList[this.stockList.length - 1]
            this.currentStockInfo = this.stockCodeList.find(
              item => item.stock_code === this.selectedCode
            ) || {}
            // 使用setTimeout确保DOM已更新
            setTimeout(() => {
              this.renderChart()
            }, 100)
          } else {
            this.$message.info('该股票暂无AD数据')
          }
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        this.$message.error('查询失败: ' + (error.message || '网络错误'))
        console.error('fetchData error:', error)
        // 清空数据避免显示错误数据
        this.stockList = []
        this.currentData = {}
      } finally {
        this.isQuerying = false
      }
    },

    renderChart() {
      if (!this.$refs.adChart || this.stockList.length === 0) {
        return
      }

      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(this.$refs.adChart)

      const dates = this.stockList.map(item => item.date)
      const closeData = this.stockList.map(item => item.close)
      const volumeData = this.stockList.map(item => item.volume)
      const adlData = this.stockList.map(item => item.adl)
      const adlMA5Data = this.stockList.map(item => item.adl_ma5)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: function(params) {
            let result = params[0].axisValue + '<br/>'
            params.forEach(param => {
              let value = param.value
              if (typeof value === 'number') {
                if (param.seriesName === '成交量') {
                  value = value.toLocaleString()
                } else if (param.seriesName === 'ADL' || param.seriesName === 'ADL MA5') {
                  if (Math.abs(value) >= 100000000) {
                    value = (value / 100000000).toFixed(2) + '亿'
                  } else if (Math.abs(value) >= 10000) {
                    value = (value / 10000).toFixed(2) + '万'
                  } else {
                    value = value.toLocaleString()
                  }
                } else {
                  value = value.toLocaleString()
                }
              }
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['收盘价', 'ADL', 'ADL MA5', '成交量'],
          textStyle: {
            color: '#94a3b8'
          }
        },
        grid: [
          {
            left: '3%',
            right: '4%',
            top: '8%',
            height: '45%'
          },
          {
            left: '3%',
            right: '4%',
            top: '60%',
            height: '30%'
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: dates,
            boundaryGap: false,
            gridIndex: 0,
            axisLine: {
              lineStyle: {
                color: '#475569'
              }
            },
            axisLabel: {
              color: '#94a3b8'
            }
          },
          {
            type: 'category',
            data: dates,
            boundaryGap: true,
            gridIndex: 1,
            axisLine: {
              lineStyle: {
                color: '#475569'
              }
            },
            axisLabel: {
              color: '#94a3b8'
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            scale: true,
            gridIndex: 0,
            name: '收盘价',
            nameTextStyle: {
              color: '#64748b'
            },
            position: 'left',
            axisLine: {
              lineStyle: {
                color: '#475569'
              }
            },
            axisLabel: {
              color: '#94a3b8'
            },
            splitLine: {
              lineStyle: {
                color: '#334155'
              }
            }
          },
          {
            type: 'value',
            scale: true,
            gridIndex: 0,
            name: 'ADL',
            nameTextStyle: {
              color: '#64748b'
            },
            position: 'right',
            axisLine: {
              lineStyle: {
                color: '#475569'
              }
            },
            axisLabel: {
              color: '#94a3b8',
              formatter: function(value) {
                if (Math.abs(value) >= 100000000) {
                  return (value / 100000000).toFixed(1) + '亿'
                } else if (Math.abs(value) >= 10000) {
                  return (value / 10000).toFixed(1) + '万'
                }
                return value.toFixed(0)
              }
            },
            splitLine: {
              show: false
            }
          },
          {
            type: 'value',
            scale: true,
            gridIndex: 1,
            name: '成交量',
            nameTextStyle: {
              color: '#64748b'
            },
            axisLine: {
              lineStyle: {
                color: '#475569'
              }
            },
            axisLabel: {
              color: '#94a3b8',
              formatter: function(value) {
                if (Math.abs(value) >= 100000000) {
                  return (value / 100000000).toFixed(1) + '亿'
                } else if (Math.abs(value) >= 10000) {
                  return (value / 10000).toFixed(1) + '万'
                }
                return value.toFixed(0)
              }
            },
            splitLine: {
              lineStyle: {
                color: '#334155'
              }
            }
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: 50,
            end: 100
          },
          {
            xAxisIndex: [0, 1],
            start: 50,
            end: 100,
            height: 20,
            bottom: 5,
            borderColor: 'transparent',
            textStyle: {
              color: '#94a3b8'
            },
            handleStyle: {
              color: '#6366f1'
            }
          }
        ],
        series: [
          {
            name: 'ADL',
            type: 'line',
            data: adlData,
            smooth: true,
            xAxisIndex: 0,
            yAxisIndex: 1,
            lineStyle: {
              color: '#06b6d4',
              width: 2
            },
            itemStyle: {
              color: '#06b6d4'
            },
            showSymbol: false
          },
          {
            name: 'ADL MA5',
            type: 'line',
            data: adlMA5Data,
            smooth: true,
            xAxisIndex: 0,
            yAxisIndex: 1,
            lineStyle: {
              color: '#fbbf24',
              width: 1.5
            },
            showSymbol: false
          },
          {
            name: '收盘价',
            type: 'line',
            data: closeData,
            smooth: true,
            xAxisIndex: 0,
            yAxisIndex: 0,
            lineStyle: {
              color: '#22c55e',
              width: 2
            },
            itemStyle: {
              color: '#22c55e'
            },
            showSymbol: false
          },
          {
            name: '成交量',
            type: 'bar',
            data: volumeData,
            xAxisIndex: 1,
            yAxisIndex: 2,
            itemStyle: {
              color: function(params) {
                const dataIndex = params.dataIndex
                if (dataIndex > 0) {
                  const prevClose = closeData[dataIndex - 1]
                  const currClose = closeData[dataIndex]
                  if (currClose > prevClose) {
                    return 'rgba(34, 197, 94, 0.7)'
                  } else if (currClose < prevClose) {
                    return 'rgba(239, 68, 68, 0.7)'
                  }
                }
                return 'rgba(148, 163, 184, 0.5)'
              }
            }
          }
        ]
      }

      this.chart.setOption(option)

      // 响应式调整
      window.addEventListener('resize', () => {
        this.chart && this.chart.resize()
      })
    },

    resetFilters() {
      this.selectedCode = ''
      const today = new Date()
      const endDate = today.toISOString().split('T')[0]
      this.startDate = '2025-01-01'
      this.endDate = endDate
      this.stockList = []
      this.currentData = {}
      this.currentStockInfo = {}
      this.isQuerying = false
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },

    getValueClass(value) {
      if (!value && value !== 0) return ''
      if (value > 0) return 'value-positive'
      if (value < 0) return 'value-negative'
      return 'value-neutral'
    },

    getCLVClass(clv) {
      if (!clv && clv !== 0) return ''
      if (clv > 0) return 'clv-positive'
      if (clv < 0) return 'clv-negative'
      return 'clv-neutral'
    },

    getADLClass(adl, adlMA5) {
      if (!adl || !adlMA5) return ''
      if (adl > adlMA5) return 'value-positive'
      if (adl < adlMA5) return 'value-negative'
      return 'value-neutral'
    },

    getTrendTagType(trend) {
      const typeMap = {
        '上升': 'success',
        '下降': 'danger',
        '震荡': 'info'
      }
      return typeMap[trend] || 'info'
    },

    formatVolume(value) {
      if (!value) return '-'
      const num = Number(value)
      if (Math.abs(num) >= 100000000) {
        return (num / 100000000).toFixed(2) + '亿'
      } else if (Math.abs(num) >= 10000) {
        return (num / 10000).toFixed(2) + '万'
      }
      return num.toLocaleString()
    },

    formatADL(value) {
      if (!value && value !== 0) return '-'
      const num = Number(value)
      if (Math.abs(num) >= 100000000) {
        return (num / 100000000).toFixed(2) + '亿'
      } else if (Math.abs(num) >= 10000) {
        return (num / 10000).toFixed(2) + '万'
      }
      return num.toLocaleString()
    },

    formatCLV(value) {
      if (!value && value !== 0) return '-'
      const num = Number(value)
      return num.toFixed(4)
    }
  }
}
</script>

<style scoped>
.stock-ad {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.title-icon svg {
  width: 28px;
  height: 28px;
}

.page-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-decoration {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  letter-spacing: 0.5px;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
}

.filter-select {
  width: 300px;
}

.filter-bar :deep(.el-select .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-select .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-select__placeholder) {
  color: #64748b;
}

.filter-bar :deep(.el-date-picker) {
  width: 200px;
}

.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

/* 图表容器 */
.chart-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.chart-header {
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.stock-info h2 {
  margin: 0 0 16px 0;
  font-size: 24px;
  color: #e2e8f0;
}

.latest-data {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.data-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.data-item label {
  color: #64748b;
  font-size: 14px;
}

.data-item strong {
  font-size: 16px;
  color: #e2e8f0;
}

.ad-chart {
  height: 800px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  padding: 20px;
}

/* 折叠面板 */
.table-collapse {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
}

.table-collapse :deep(.el-collapse-item__header) {
  color: #e2e8f0;
  background: transparent;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.table-collapse :deep(.el-collapse-item__wrap) {
  background: transparent;
}

/* 表格样式 */
.stock-table {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  overflow: hidden;
}

.stock-table :deep(.el-table__header-wrapper) {
  background: rgba(15, 23, 42, 0.5);
}

.stock-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.8) !important;
  color: #a5b4fc;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.stock-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.stock-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.stock-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

/* 数值样式 */
.value-positive {
  color: #22c55e;
  font-weight: 600;
}

.value-negative {
  color: #ef4444;
  font-weight: 600;
}

.value-neutral {
  color: #94a3b8;
}

/* CLV 样式 */
.clv-positive {
  color: #06b6d4;
  font-weight: 600;
}

.clv-negative {
  color: #f43f5e;
  font-weight: 600;
}

.clv-neutral {
  color: #94a3b8;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 120px 24px;
  color: #64748b;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}
</style>

<style>
/* 日期选择器面板深色主题覆盖 - 非scoped样式以确保生效 */
.el-picker-panel {
  background: rgba(15, 23, 42, 0.98) !important;
  border: 1px solid rgba(99, 102, 241, 0.3) !important;
}

.el-picker-panel * {
  color: #e2e8f0 !important;
}
</style>
